import type { LucideIcon } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export type ListViewSwitcherOption<T extends string = string> = {
  value: T
  label: string
  icon?: LucideIcon
}

export type ListViewSwitcherProps<T extends string = string> = {
  value: T
  onValueChange: (value: T) => void
  options: ListViewSwitcherOption<T>[]
  ariaLabel?: string
  className?: string
  /** `icon` — icon-only (default). `labeled` — icon + visible label text. */
  variant?: 'icon' | 'labeled'
}

export function ListViewSwitcher<T extends string = string>({
  value,
  onValueChange,
  options,
  ariaLabel = 'View',
  className,
  variant = 'icon',
}: ListViewSwitcherProps<T>) {
  const isLabeled = variant === 'labeled'

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cn(
        'inline-flex shrink-0 items-center gap-0.5 rounded-md border border-border/60 bg-muted/20 p-0.5',
        !isLabeled && 'h-8',
        className,
      )}
    >
      {options.map((option) => {
        const Icon = option.icon
        const selected = value === option.value
        return (
          <Button
            key={option.value}
            type="button"
            variant="ghost"
            size={isLabeled ? 'sm' : 'iconXs'}
            onClick={() => onValueChange(option.value)}
            aria-pressed={selected}
            aria-label={option.label}
            title={isLabeled ? undefined : option.label}
            className={cn(
              isLabeled ? 'h-7 gap-1.5 px-2.5' : undefined,
              selected
                ? 'bg-background text-foreground shadow-sm hover:bg-background hover:text-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {Icon ? <Icon className="size-4 shrink-0" aria-hidden /> : null}
            {isLabeled ? (
              <span className="text-xs font-medium">{option.label}</span>
            ) : Icon ? null : (
              option.label
            )}
          </Button>
        )
      })}
    </div>
  )
}
